import { AxiosErrorResponse } from '@/@types/Errors';
import {
  EditProductRequest,
  ProductDetailsResponse,
  ProductRole,
} from '@/@types/Products';
import { useAuth } from '@/auth/Auth';
import { usePermissions } from '@/auth/PermissionsContext';
import DeliverableSection from '@/components/Products/deliverable-section';
import RegulatoryDocumentsSection from '@/components/Products/regulatory-documents-section';
import DetailsSection from '@/components/Products/details-section';
import { DuplicateProductModal } from '@/components/Products/DuplicateProductModal';
import RenderSkeleton from '@/components/Products/edit-product-skeleton';
import { ProductStatusSection } from '@/components/Products/product-status-section';
import { SupportEmailVerificationDialog } from '@/components/shared/SupportEmailVerification';
import { Input } from '@/components/shared/Input/Input';
import { PhoneInput } from '@/components/shared/PhoneInput/PhoneInput';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { ProductsService } from '@/services/products.service';
import { normalizeRefundDeadlineDays } from '@/constants/refundDeadline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Loader2, Info } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router-dom';
import { useEditProductSchema } from './Affiliate/hooks/useEditProductSchema';

export default function EditProduct() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation([
    'common',
    'onboarding',
    'validation',
    'affiliates',
  ]);
  const { authData } = useAuth();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [productNamePreview, setProductNamePreview] = useState('');
  const [hasImageChanged, setHasImageChanged] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [verifiedSupportEmail, setVerifiedSupportEmail] = useState<string | null>(
    null,
  );
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] =
    useState<EditProductRequest | null>(null);
  const pendingSubmitDataRef = useRef<EditProductRequest | null>(null);
  const shouldSubmitAfterVerifyRef = useRef(false);
  const initialSupportEmailRef = useRef<string | null>(null);

  const normalizeEmail = (value: string | null | undefined) =>
    (value || '').trim().toLowerCase();

  const hasSupportEmailChanged = useCallback(
    (email: string | null | undefined) =>
      normalizeEmail(email) !== normalizeEmail(initialSupportEmailRef.current),
    [],
  );

  const isSupportEmailVerificationError = (
    error: AxiosError<AxiosErrorResponse>,
  ) => {
    if (error.response?.status !== 422) return false;

    const fieldErrors = error.response?.data?.errors?.email_support;
    const fieldMessage = Array.isArray(fieldErrors)
      ? fieldErrors.join(' ').toLowerCase()
      : '';
    const message = `${error.response?.data?.message ?? ''}`.toLowerCase();
    const combined = `${fieldMessage} ${message}`;

    return (
      combined.includes('verific') ||
      combined.includes('código') ||
      combined.includes('codigo') ||
      combined.includes('otp') ||
      (combined.includes('support email') && combined.includes('verif')) ||
      (combined.includes('e-mail de suporte') && combined.includes('verif'))
    );
  };

  const openSupportEmailVerification = useCallback(
    (data: EditProductRequest) => {
      shouldSubmitAfterVerifyRef.current = true;
      pendingSubmitDataRef.current = data;
      setPendingSubmitData(data);
      setVerifiedSupportEmail(null);
      setIsVerifyDialogOpen(true);
    },
    [],
  );

  const { product, isLoadingProductDetail } = useOutletContext<{
    product: ProductDetailsResponse;
    isLoadingProductDetail: boolean;
  }>();

  const { data: productsSummary } = useQuery({
    queryKey: ['products', 'summary-for-edit'],
    queryFn: async () =>
      ProductsService.getProducts({
        currentPage: 1,
        query: '',
        status: 'all',
      }),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  const isOnlyProduct =
    (productsSummary?.meta?.total ?? productsSummary?.data?.length ?? 0) === 1;

  const shouldShowUrlPages =
    isOnlyProduct || Boolean(product?.support_url || product?.privacy_terms_url);

  const { register, handleSubmit, setValue, watch, reset, errors, formValues, setError, clearErrors } =
    useEditProductSchema(t, product);

  useEffect(() => {
    if (product && !isLoadingProductDetail) {
      const initialValues = {
        name: product.name,
        description: product.description,
        phone_prefix: product.phone_prefix || '55',
        whatsapp_support: product.whatsapp_support,
        email_support: product.email_support,
        sales_page: product.sales_page,
        thankyou_url: product.thankyou_url || '',
        support_url: product.support_url || '',
        privacy_terms_url: product.privacy_terms_url || '',
        status: product.status,
        category_id: product.category.id,
        product_extras: product.product_extras || '',
        product_url: product.product_url || '',
        product_email: product.product_email || '',
        product_password: product.product_password || '',
        category_type: product.category.type,
        product_delivery: product.product_delivery || '',
        refund_deadline_days: normalizeRefundDeadlineDays(
          product.refund_deadline_days,
          product.category?.type,
        ),
      };

      reset(initialValues);
      setInitialFormValues(initialValues);
      setVerifiedSupportEmail(product.email_support?.trim() || null);
      initialSupportEmailRef.current = product.email_support?.trim() || null;
      setProductNamePreview(product.name);
      setHasFormChanged(false);
    }
  }, [product, reset]);

  useEffect(() => {
    const subscription = watch((value) => {
      if (value.name) {
        setProductNamePreview(value.name);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (!initialFormValues || !formValues) return;

    const formFieldsChanged =
      formValues?.name !== initialFormValues.name ||
      formValues?.description !== initialFormValues.description ||
      formValues?.phone_prefix !== initialFormValues.phone_prefix ||
      formValues?.whatsapp_support !== initialFormValues.whatsapp_support ||
      formValues?.email_support !== initialFormValues.email_support ||
      formValues?.sales_page !== initialFormValues.sales_page ||
      formValues?.thankyou_url !== initialFormValues.thankyou_url ||
      formValues?.support_url !== initialFormValues.support_url ||
      formValues?.privacy_terms_url !== initialFormValues.privacy_terms_url ||
      formValues?.category_id !== initialFormValues.category_id ||
      formValues?.product_extras !== initialFormValues.product_extras ||
      formValues?.product_url !== initialFormValues.product_url ||
      formValues?.product_email !== initialFormValues.product_email ||
      formValues?.product_password !== initialFormValues.product_password ||
      formValues?.product_delivery !== initialFormValues.product_delivery ||
      formValues?.status !== initialFormValues.status ||
      Number(formValues?.refund_deadline_days) !== Number(initialFormValues.refund_deadline_days);

    const hasChanged = formFieldsChanged || hasImageChanged || files.length > 0;
    setHasFormChanged(hasChanged);
  }, [files.length, hasImageChanged, formValues, initialFormValues]);

  const handlePhoneChange = useCallback(
    (value: string, prefix: string) => {
      setValue('whatsapp_support', value);
      setValue('phone_prefix', prefix);
    },
    [setValue]
  );

  const handleUpload = useCallback(() => {
    if (files.length > 0) {
      files.forEach((file) => uploadFiles(file));
    }
  }, [files]);

  const { mutate: editProduct, isPending: isEditingProduct } = useMutation({
    mutationFn: (data: EditProductRequest) => ProductsService.editProduct(data),
    onMutate({ name }) {
      const { cached } = updateProductNameCache({ name });
      return { previousProduct: cached };
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [`get-product-${id}`] });
      setHasFormChanged(false);
      setFiles([]);
      setVerifiedSupportEmail(variables.email_support ?? null);
      initialSupportEmailRef.current = variables.email_support?.trim() || null;
      toast({
        description: t('product_successfully_edited'),
        variant: 'success',
      });
    },
    onError: (error: AxiosError<AxiosErrorResponse>, variables, context) => {
      if (context?.previousProduct) {
        updateProductNameCache(context.previousProduct);
      }

      if (
        isSupportEmailVerificationError(error) &&
        variables &&
        hasSupportEmailChanged(variables.email_support)
      ) {
        openSupportEmailVerification(variables);
        return;
      }

      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || t('data_submission_error'),
      });
    },
  });

  const { mutate: uploadFiles, isPending: isUploadingFiles } = useMutation({
    mutationFn: async (file: File) => {
      await ProductsService.createDeliverables({
        files: [file],
        productId: id,
      });
      return file.name;
    },
    onSuccess: async (fileName) => {
      await queryClient.invalidateQueries({
        queryKey: [`list-deliverables-${id}`],
      });
      toast({
        description: t('files_successfully_uploaded'),
        variant: 'success',
      });
      setFiles((prev) => prev.filter((f) => f.name !== fileName));
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || t('data_submission_error'),
      });
    },
  });

  const { mutate: uploadProductImage, isPending: isUploadingImage } =
    useMutation({
      mutationFn: (image: File) =>
        ProductsService.uploadProductImage({ image, productId: id }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [`get-product-${id}`],
        });
        toast({
          description: t('image_successfully_updated'),
          variant: 'success',
        });
        setHasImageChanged(false);
      },
      onError: (error: AxiosError<AxiosErrorResponse>) => {
        toast({
          variant: 'error',
          description:
            error?.response?.data?.message || t('data_submission_error'),
        });
      },
    });

  function updateProductNameCache({ name }: { name: string }): any {
    const cached = queryClient.getQueryData<ProductDetailsResponse>([
      `get-product-${id}`,
    ]);

    if (cached) {
      queryClient.setQueryData([`get-product-${id}`], {
        ...cached,
        name,
      });
    }

    return { cached };
  }

  const isProducer = product?.relationship?.role === ProductRole.PRODUCER;
  const isAffiliate = product?.relationship?.role === ProductRole.AFFILIATE;

  const { hasPermission } = usePermissions();

  const canEdit = isProducer;

  const canChangeStatus = canEdit && hasPermission('products_edit.change_product_status');
  const canEditDetails = canEdit && hasPermission('products_edit.edit_product_details');
  const canUploadImage = canEdit && hasPermission('products_edit.upload_product_image');
  const canUploadDeliverables = canEdit && hasPermission('products_edit.upload_product_deliverables');
  const canEditAccessData = canEdit && hasPermission('products_edit.edit_product_access_data');
  const canEditSupportInfo = canEdit && hasPermission('products_edit.edit_product_support_info');
  const canDuplicate = canEdit && hasPermission('products_edit.duplicate_product');
  const canUploadRegulatoryDocs = canEdit && hasPermission('products_edit.upload_regulatory_documents');
  const canDeleteRegulatoryDocs = canEdit && hasPermission('products_edit.delete_regulatory_documents');

  const enableAccessInputs = product?.category?.type === 'digital';
  const isNutraProduct = product?.category?.type === 'pharmaceutical';

  const submitProduct = useCallback(
    (data: EditProductRequest) => {
      data.id = id;

      if (hasImageChanged && data.image) {
        uploadProductImage(data.image);
      }

      data.thankyou_url = data.thankyou_url === '' ? null : data.thankyou_url;
      data.support_url = data.support_url === '' ? null : data.support_url;
      data.privacy_terms_url =
        data.privacy_terms_url === '' ? null : data.privacy_terms_url;

      editProduct(data);
      handleUpload();
    },
    [editProduct, handleUpload, hasImageChanged, id, uploadProductImage],
  );

  const needsSupportEmailVerification = useCallback(
    (email: string | null | undefined) =>
      normalizeEmail(email) !== normalizeEmail(verifiedSupportEmail),
    [verifiedSupportEmail],
  );

  const onSubmit = (data: EditProductRequest) => {
    if (
      canEditSupportInfo &&
      hasSupportEmailChanged(data.email_support) &&
      needsSupportEmailVerification(data.email_support)
    ) {
      openSupportEmailVerification(data);
      return;
    }

    submitProduct(data);
  };

  const handleSupportEmailVerified = useCallback(
    (email: string) => {
      const shouldSubmit = shouldSubmitAfterVerifyRef.current;
      shouldSubmitAfterVerifyRef.current = false;

      const verified = email.trim();
      setVerifiedSupportEmail(verified);
      initialSupportEmailRef.current = verified;
      clearErrors('email_support');
      setValue('email_support', verified);

      const dataToSubmit = pendingSubmitDataRef.current;
      pendingSubmitDataRef.current = null;
      setPendingSubmitData(null);

      if (shouldSubmit && dataToSubmit) {
        submitProduct({ ...dataToSubmit, email_support: verified });
      }
    },
    [clearErrors, setValue, submitProduct],
  );

  const handleVerifyDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      shouldSubmitAfterVerifyRef.current = false;
      pendingSubmitDataRef.current = null;
      setPendingSubmitData(null);
      setVerifiedSupportEmail(initialSupportEmailRef.current);
    }

    setIsVerifyDialogOpen(open);
  }, []);

  const emailNeedsVerification =
    canEditSupportInfo &&
    needsSupportEmailVerification(formValues?.email_support);

  return (
    <>
      <section className='m-auto max-w-[1032px] md:px-10 xl:px-0 w-full'>
        <section className='gap-6 justify-between m-0 w-full flex-col lg:flex-row flex mt-12'>
          <article className='space-y-1 lg:w-[328px]'>
            <h2 className='text-colorText font-medium'>
              {t('digital_product')}
            </h2>
            <p className='text-muted text-sm'>{t('digital_subtitle')}</p>
          </article>

          <Card className='w-full p-4 lg:p-6 lg:max-w-[680px] bg-[09090b]'>
            {isLoadingProductDetail ? (
              <RenderSkeleton count={5} />
            ) : (
              <>
                {canChangeStatus && (
                  <ProductStatusSection
                    product={product}
                    authData={authData}
                    formValues={formValues}
                    setValue={setValue}
                    t={t}
                  />
                )}

                <DetailsSection
                  canEditDetails={canEditDetails}
                  canUploadImage={canUploadImage}
                  isUploadingImage={isUploadingImage}
                  t={t}
                  productNamePreview={productNamePreview}
                  register={register}
                  errors={errors}
                  formValues={formValues}
                  hasImageChanged={hasImageChanged}
                  product={product}
                  setValue={setValue}
                  setHasImageChanged={setHasImageChanged}
                  showUrlPages={shouldShowUrlPages}
                />
              </>
            )}
          </Card>
        </section>

        {!isAffiliate  && (
          <DeliverableSection
            isUploadingFiles={isUploadingFiles}
            canUploadDeliverables={canUploadDeliverables}
            canEditAccessData={canEditAccessData}
            enableAccessInputs={enableAccessInputs}
            errors={errors}
            files={files}
            setFiles={setFiles}
            id={id}
            isLoadingProductDetail={isLoadingProductDetail}
            register={register}
            t={t}
            setValue={setValue}
            watch={watch}
          />
        )}

        {!isAffiliate && isNutraProduct && id && (
          <RegulatoryDocumentsSection
            productId={id}
            canUpload={canUploadRegulatoryDocs}
            canDelete={canDeleteRegulatoryDocs}
            isLoadingProductDetail={isLoadingProductDetail}
          />
        )}

        <section className='gap-6 w-full mb-[120px] flex mt-12 flex-col lg:flex-row'>
          <article className='space-y-1 lg:w-[328px]'>
            <h2 className='text-colorText font-medium'>{t('support')}</h2>
            <p className='text-muted text-sm'>{t('support_subtitle')}</p>
          </article>

          <Card className='w-full p-4 lg:p-6 lg:max-w-[680px] bg-[09090b]'>
            {isLoadingProductDetail ? (
              <RenderSkeleton count={2} />
            ) : (
              <section className='flex gap-6 flex-col xl:flex-row'>
                <div className='w-full'>
                  <Input
                    {...register('email_support')}
                    label={t('supportEmailLabel')}
                    helperText={errors?.email_support?.message}
                    disabled={!canEditSupportInfo}
                    data-cy='product-support-email-input'
                  />
                  {emailNeedsVerification && !errors?.email_support?.message && (
                    <p className='flex items-start gap-2 text-sm text-muted mt-2'>
                      <Info className='h-4 w-4 shrink-0 mt-0.5 text-[#A9D342]' />
                      <span>{t('editProductSupportEmailVerifyOnSave')}</span>
                    </p>
                  )}
                </div>

                <PhoneInput
                  label={t('supportWhatsAppLabel')}
                  onChangePhone={handlePhoneChange}
                  phone_prefix={formValues?.phone_prefix}
                  value={formValues?.whatsapp_support}
                  placeholder={t('supportWhatsAppPlaceholder')}
                  helperText={errors?.whatsapp_support?.message}
                  disabled={!canEditSupportInfo}
                  data-cy='product-whatsapp-input'
                />
              </section>
            )}
          </Card>
        </section>

        {(canEdit) && (
          <footer className='w-full border-t border-t-colorBorder bg-black fixed bottom-0 left-0 p-4 lg:p-6'>
            <div className='flex w-full md:justify-end justify-center gap-6 items-center'>
              {canDuplicate && <DuplicateProductModal productId={id} />}
              <Button
                variant='default'
                className='w-full md:max-w-[148px]'
                onClick={handleSubmit(onSubmit)}
                data-cy='save-product-button'
                disabled={
                  authData?.onboarding_step !== 'finished' ||
                  product?.status === 'blocked' ||
                  isEditingProduct ||
                  !hasFormChanged
                }
              >
                {isEditingProduct ? (
                  <Loader2 className='animate-spin' />
                ) : (
                  t('save_changes')
                )}
              </Button>
            </div>
          </footer>
        )}
      </section>

      <SupportEmailVerificationDialog
        open={isVerifyDialogOpen}
        onOpenChange={handleVerifyDialogOpenChange}
        email={(pendingSubmitData?.email_support || formValues?.email_support || '').trim()}
        productName={product?.name}
        verifiedEmail={verifiedSupportEmail}
        onVerified={handleSupportEmailVerified}
        onInvalidDomain={() => {
          setError('email_support', {
            type: 'manual',
            message: t('supportEmailInvalidDomain'),
          });
        }}
      />
    </>
  );
}